// ReJoinScreen — entry UI for "URL pathname is `/<uuid>`" branch.
//
// Flow:
//   1. mount → call `dispatcher.lookupRoomServer(roomId)`.
//   2. on 'ok': render display-name form. submit → `join({ wsUrl, roomId, ... })`.
//      `?name=<displayName>` も URL にあれば auto-join (既存 M1 互換).
//   3. on 'not-found': show "ルームが見つかりません" via ErrorScreen.
//   4. on 'unavailable' / 'error': show server / network error via ErrorScreen.
//
// adminToken は `AdminTokenStore.get(roomId)` で取り出すが Phase 4 では
// 認証で使う経路はないので保存だけが目的 (Phase 6 で消費).
//
// 2026-05 改訂: 着地点 URL を `?room=<uuid>` → pathname `/<uuid>` に切替.
// query `?name=<displayName>` (auto-join) は維持.

import {
  MAX_DISPLAY_NAME_LENGTH,
  MAX_ENTRY_PASSWORD_LENGTH,
  MIN_DISPLAY_NAME_LENGTH,
  normalizeEntryPassword,
} from '@oekaki/protocol';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { reportLayoutSwitch } from '../application/layoutSwitchAnalytics.js';
import type {
  EntryInfoMember,
  EntryInfoPreview,
  FetchEntryInfoOutcome,
  LookupRoomServerOutcome,
} from '../infrastructure/dispatcher/DispatcherClient.js';
import { createDisplayNameStore } from '../infrastructure/storage/DisplayNameStore.js';
import { AlphaDataWarningNotice } from './AlphaWarningBanner.js';
import { ErrorScreen } from './ErrorScreen.js';
import { useSession } from './hooks/useSession.js';
import { LayoutChoiceCards } from './layouts/LayoutChoiceCards.js';
import { useLayoutPreference } from './layouts/useLayoutPreference.js';
import { formatReopenText } from './maintenanceText.js';
import { hardReloadToHome } from './urlNavigation.js';

type LookupState =
  | { readonly t: 'looking-up' }
  | {
      readonly t: 'ready';
      readonly wsUrl: string;
      /**
       * 入室「あいことば」(owner-vs-entry-password-2026-06-13.md): この部屋が
       * visitor 入室に `auth.entryPassword` を要求するか. lookup 応答由来
       * (`roomMeta.entryPasswordRequired`、旧 server は false fallback 済).
       */
      readonly entryPasswordRequired: boolean;
    }
  | {
      readonly t: 'not-found';
    }
  | {
      readonly t: 'unavailable';
      readonly reason: 'no_server' | 'server_down' | 'unknown';
    }
  /**
   * Phase 7 Task 7-6: dispatcher が「メンテナンス中」と返した. `waitUntil`
   * は epoch ms (再開時刻、null は不定). UI は ErrorScreen で待機 + 自動 retry.
   */
  | {
      readonly t: 'maintenance';
      readonly waitUntilMs: number | null;
    }
  | {
      readonly t: 'error';
      readonly cause: 'network' | 'invalid' | 'http-status';
      readonly status?: number;
    };

const initialNameFromQuery = (): string => {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('name')?.trim() ?? '';
};

const goToCreateScreen = (): void => {
  // Hard reload so the App switches back to HomePage cleanly (replaceState
  // alone would not re-evaluate App's URL parse, and the WSClient may carry
  // stale close state from a previous attempt — `location.assign` is the
  // safest hammer for the entry-error branch).
  hardReloadToHome();
};

export interface ReJoinScreenProps {
  readonly roomId: string;
}

export const ReJoinScreen = ({ roomId }: ReJoinScreenProps): React.ReactNode => {
  const {
    dispatcher,
    join,
    transport,
    adminTokens,
    entryPasswordRejection,
    clearEntryPasswordRejection,
    displayNames,
  } = useSession();
  // 入室あいことば (owner-vs-entry-password-2026-06-13.md): admin_token を
  // 持って入る (= オーナー) 場合は打たない — 欄自体を出さない/送らない。
  // 毎 render 読み直す (他画面での localStorage 変化を拾うため、RoomSettingsModal
  // の useAdminToken と同じ流儀).
  const isAdminMode = adminTokens.get(roomId) !== null;
  // Classic ⇄ Modern 選択. setLayout が即 localStorage に永続化し、入室後 App の
  // レイアウト解決 (resolveLayout) が同じ store を読むので、ここで選んだ表示で始まる。
  // showToggle は viewport 幅 (resize 追従) でスマホなら false (= 非表示)。
  // §9 layout-switch analytics: この画面では join 前 (socket 未 open) なので
  // reportLayoutSwitch は毎回 no-op になるが、App.tsx (入室後トグル) と同じ
  // onSwitch 配線にしておく (呼び出し側を分岐させない — Transport の open 判定は
  // reportLayoutSwitch 側の責務).
  const { layout, setLayout, showToggle } = useLayoutPreference({
    onSwitch: (next) => reportLayoutSwitch(transport, next),
  });
  const initialName = useMemo(initialNameFromQuery, []);
  // 名前記憶: `?name=` が無いとき、端末に覚えた前回の名前を欄の初期値にする.
  // auto-join には使わない (initialName = `?name=` のみが auto-join 対象 —
  // 入室ボタンを押すのは本人). 読み戻しの長さ検証は store 側.
  const savedName = useMemo(
    () => (initialName.length === 0 ? (displayNames ?? createDisplayNameStore()).get() : null),
    [initialName, displayNames],
  );
  const [displayName, setDisplayName] = useState(initialName || (savedName ?? ''));
  // 覚えた名前を入れたときだけ、欄の mount 時に全選択する (共有端末で前の人の名前の
  // まま気づかず入る事故を減らす。打ち始めれば丸ごと置き換わる). iOS Safari / Chrome は
  // focus 直後の select() を無視することがあるので、setSelectionRange と次フレームでの
  // 再実行を併用する.
  // × (名前を消す) ボタンから欄へ focus を戻すための参照.
  const nameInputEl = useRef<HTMLInputElement | null>(null);
  const nameInputRef = useCallback(
    (el: HTMLInputElement | null) => {
      nameInputEl.current = el;
      if (el === null || savedName === null) return;
      const selectAll = (): void => {
        el.focus();
        el.select();
        el.setSelectionRange(0, el.value.length);
      };
      selectAll();
      setTimeout(selectAll, 0);
    },
    [savedName],
  );
  const [state, setState] = useState<LookupState>({ t: 'looking-up' });
  const [autoTried, setAutoTried] = useState(false);
  // 入室あいことば: 入力値 + エラー表示 (欄の下). エラーは「入力必須」ローカル
  // 検証と、server 由来の `invalid-entry-password` 巻き戻しの両方で使う.
  const [entryPassword, setEntryPassword] = useState('');
  const [entryPasswordError, setEntryPasswordError] = useState<string | null>(null);
  // entry-preview-members-2026-07-21: 入室画面のプレビュー + 参加者一覧.
  // null = 取得中. 取得失敗 (outcome 'error') は何も出さない (graceful degrade、
  // 入室フォームの動作を一切阻害しない). ポーリングなし = mount 時 1 回のみ.
  const [entryInfo, setEntryInfo] = useState<FetchEntryInfoOutcome | null>(null);

  // Lookup once on mount. roomId is captured by closure; if URL changes the
  // App re-mounts ReJoinScreen with a new key implicitly (or full reload).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const out: LookupRoomServerOutcome = await dispatcher.lookupRoomServer(roomId);
      if (cancelled) return;
      switch (out.outcome) {
        case 'ok':
          setState({
            t: 'ready',
            wsUrl: out.wsUrl,
            entryPasswordRequired: out.roomMeta.entryPasswordRequired,
          });
          return;
        case 'not-found':
          setState({ t: 'not-found' });
          return;
        case 'unavailable':
          setState({ t: 'unavailable', reason: out.reason });
          return;
        case 'maintenance':
          setState({ t: 'maintenance', waitUntilMs: out.waitUntil });
          return;
        case 'error':
          setState({ t: 'error', cause: out.cause, status: out.status });
          return;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatcher, roomId]);

  // entry-info を lookup と並行に mount 時 1 回だけ取得する. 失敗しても入室
  // フォームは通常動作 (fetchEntryInfo が fail-silent に 'error' へ潰す).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const out = await dispatcher.fetchEntryInfo(roomId);
      if (cancelled) return;
      setEntryInfo(out);
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatcher, roomId]);

  // 入室あいことば不一致で入室画面に巻き戻された直後の引き継ぎ. mount ごとに
  // 1 回だけ消費する (= roomId が一致すれば表示名を引き継ぎつつエラーを出し、
  // 消費済にする). SessionProvider 側は `mark-idle` dispatch 前に
  // `entryPasswordRejection` state をセット済なので、ReJoinScreen の
  // 「新しい」mount (= 巻き戻し後の URL 駆動 remount) がこれを拾う.
  // clearEntryPasswordRejection / roomId は安定なので依存に含めない.
  // biome-ignore lint/correctness/useExhaustiveDependencies: entryPasswordRejection の変化だけを契機にした mount 時 1 回消費が目的.
  useEffect(() => {
    if (entryPasswordRejection === null) return;
    if (entryPasswordRejection.roomId !== roomId) return;
    setDisplayName(entryPasswordRejection.displayName);
    setEntryPasswordError('あいことばが違います');
    clearEntryPasswordRejection();
  }, [entryPasswordRejection]);

  // Auto-join when lookup succeeded AND the URL carried a usable display name.
  //
  // 入室あいことば: 部屋が要求する (かつ admin mode でない) ときは auto-join
  // せず入室画面へ落とす (表示名は `?name=` prefill のまま、あいことば欄が
  // 空欄で待つ). admin (= オーナー) はあいことばを打たないので通常通り
  // auto-join する.
  useEffect(() => {
    if (autoTried) return;
    if (state.t !== 'ready') return;
    if (state.entryPasswordRequired && !isAdminMode) return;
    if (
      initialName.length >= MIN_DISPLAY_NAME_LENGTH &&
      initialName.length <= MAX_DISPLAY_NAME_LENGTH
    ) {
      setAutoTried(true);
      join({ wsUrl: state.wsUrl, roomId, displayName: initialName });
    }
  }, [state, initialName, autoTried, join, roomId, isAdminMode]);

  // ─── Branch on lookup state ──────────────────────────────────────────────

  if (state.t === 'looking-up') {
    return (
      <div className="grid h-full place-items-center">
        <p className="text-sm text-[var(--color-text-muted)]">ルーム情報を取得中...</p>
      </div>
    );
  }

  if (state.t === 'not-found') {
    return (
      <ErrorScreen
        title="ルームが見つかりません"
        description="このルーム ID は存在しないか削除されています。新しいルームを作成しますか？"
        cta={{ label: '新規ルームを作成', onActivate: goToCreateScreen }}
        detail={`roomId: ${roomId}`}
      />
    );
  }

  if (state.t === 'unavailable') {
    return (
      <ErrorScreen
        title="サーバが利用不可です"
        description="しばらくしてから再度お試しください。"
        cta={{ label: '新規ルームを作成', onActivate: goToCreateScreen }}
        detail={`reason: ${state.reason}`}
      />
    );
  }

  if (state.t === 'maintenance') {
    // Phase 7 Task 7-6: メンテ中. 再開予定時刻を JST 固定で表示 (HomePage の
    // バナーと文言を共通 helper で揃える). 旧版は detail に ISO (UTC) を出して
    // いたが分かりづらいので廃止した.
    const description = `サーバはメンテナンス中です。${formatReopenText(state.waitUntilMs, Date.now())}`;
    return (
      <ErrorScreen
        title="メンテナンス中"
        description={description}
        cta={{ label: '再読み込み', onActivate: () => window.location.reload() }}
      />
    );
  }

  if (state.t === 'error') {
    const description =
      state.cause === 'network'
        ? 'ネットワークエラーです。接続を確認してから再読み込みしてください。'
        : state.cause === 'invalid'
          ? 'サーバの応答が不正です。時間を置いて再度お試しください。'
          : 'サーバでエラーが発生しました。';
    return (
      <ErrorScreen
        title="エラーが発生しました"
        description={description}
        cta={{ label: '新規ルームを作成', onActivate: goToCreateScreen }}
        detail={state.status !== undefined ? `HTTP ${state.status}` : state.cause}
      />
    );
  }

  // state.t === 'ready' — show display-name form.
  // 入室あいことば: 部屋が要求し、かつ admin mode でないときだけ必須 (欄自体も
  // このときだけ表示する — 下の JSX 参照).
  const entryPasswordNeeded = state.entryPasswordRequired && !isAdminMode;
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const trimmed = displayName.trim();
    if (trimmed.length < MIN_DISPLAY_NAME_LENGTH || trimmed.length > MAX_DISPLAY_NAME_LENGTH) {
      return;
    }
    // 正規化後 (NFKC + trim) の長さで空欄判定する — 全角スペースだけの入力を
    // 「入力あり」として送ると server が 400 で弾くため (normalizeEntryPassword
    // のコメント参照).
    const normalizedEntryPassword = normalizeEntryPassword(entryPassword);
    if (entryPasswordNeeded && normalizedEntryPassword.length === 0) {
      setEntryPasswordError('あいことばを入力してください');
      return;
    }
    setEntryPasswordError(null);
    join({
      wsUrl: state.wsUrl,
      roomId,
      displayName: trimmed,
      ...(entryPasswordNeeded ? { entryPassword: normalizedEntryPassword } : {}),
    });
  };

  return (
    <div className="grid h-full place-items-center p-4">
      <form
        onSubmit={handleSubmit}
        // 幅は 380px cap + 小画面では全幅追従 (= 360px 端末で横はみ出しさせない、
        // M1-b modern-phone-shell-2026-07-02.md。親の p-4 が左右マージンを担保)。
        className="w-full max-w-[380px] rounded border border-white/10 bg-[var(--color-panel)] p-6 shadow-xl"
        aria-label="ルーム入室フォーム"
      >
        <h1 className="mb-4 text-center text-lg font-bold tracking-wide text-[var(--color-accent)]">
          ルームに入室
        </h1>

        <p className="mb-3 text-center text-xs text-[var(--color-text-muted)]">
          ルーム ID: <span className="font-mono">{roomId.slice(0, 8)}...</span>
        </p>

        {/* entry-preview-members-2026-07-21: 少し前のキャンバス + いまいる人.
            取得中 / 取得失敗は何も出さない (フォームの主目的 = 入室を邪魔しない).
            あいことば付き部屋 (entryInfo.entryPasswordRequired) は代わりに
            一文だけ出す (キャンバス/参加者はサーバ側で隠されている). */}
        <EntryInfoSection info={entryInfo} />

        <label className="mb-4 block text-sm">
          表示名 ({MIN_DISPLAY_NAME_LENGTH}〜{MAX_DISPLAY_NAME_LENGTH}文字)
          {/* × (名前を消す): iPad の手書き入力 (スクリブル) は全選択を外して後ろに足すので、
              覚えた名前を 1 タップで消せるようにする (別の人に代わるときも同じ)。 */}
          <div className="relative mt-1">
            <input
              type="text"
              ref={nameInputRef}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={MAX_DISPLAY_NAME_LENGTH}
              required
              // biome-ignore lint/a11y/noAutofocus: ReJoin の挙動を改善するため、フォームが出た瞬間に入力できる
              autoFocus
              className="w-full rounded border border-white/30 bg-[var(--color-bg-soft)] py-1 pr-10 pl-2 text-base text-white outline-none focus:border-[var(--color-accent)]"
              placeholder="名無しさん"
            />
            {displayName !== '' && (
              <button
                type="button"
                aria-label="名前を消す"
                onClick={() => {
                  setDisplayName('');
                  nameInputEl.current?.focus();
                }}
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-lg text-[var(--color-text-muted)] hover:text-white"
              >
                ×
              </button>
            )}
          </div>
        </label>

        {/* 入室あいことば (owner-vs-entry-password-2026-06-13.md): 部屋が
            要求する (かつ admin mode でない) ときだけ出す。ラベルの
            「（パスワード）」は 2026-09-19 追記 (結論 1 の部分改定、作者判断) —
            混同回避の主策は結論 3 (オーナーは打たない = 使う人で別物化) で
            維持されるため、旧版ユーザー向けの理解補助として括弧書きを足す。
            オーナーパスワードとは別画面・常にフル表記で分ける。 */}
        {entryPasswordNeeded && (
          <div className="mb-4">
            <label className="block text-sm">
              あいことば（パスワード）
              <input
                type="text"
                value={entryPassword}
                onChange={(e) => {
                  setEntryPassword(e.target.value);
                  if (entryPasswordError !== null) setEntryPasswordError(null);
                }}
                maxLength={MAX_ENTRY_PASSWORD_LENGTH}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                className="mt-1 w-full rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1 text-base text-white outline-none focus:border-[var(--color-accent)]"
                placeholder="この部屋の入室に必要なあいことば"
              />
            </label>
            {/* エラー <span> は <label> の外 (兄弟要素) に置く — 内側に置くと
                label のテキスト内容に混ざり `getByLabelText('あいことば（パスワード）')`
                の完全一致マッチが壊れる (RoomSettingsModal の NameField と
                同じ流儀). `required` も付けない — ネイティブ制約検証だと
                submit イベント自体が発火せず下の JS 側エラー文言 (「あいことば
                を入力してください」) が出せなくなるため、検証はここで自前に行う. */}
            {entryPasswordError !== null && (
              <p className="mt-1 text-xs text-[var(--color-error)]" role="alert">
                {entryPasswordError}
              </p>
            )}
          </div>
        )}

        {/* レイアウト選択 (Classic / Modern の 2 枚モックカード). スマホ (小画面) では
            非表示 = Modern 固定 (§9.4.1 ④)。選択は即 localStorage に永続化され、入室後
            その表示で開く (resolveLayout が同じ store を読む)。 */}
        <LayoutChoiceCards current={layout} onChange={setLayout} show={showToggle} />

        {/* α 期データ削除警告 (2026-05-23 W2 公開向け). 入室すると uid が
            紐付くので、その直前に保存系注意を出す. */}
        <div className="mb-3">
          <AlphaDataWarningNotice />
        </div>

        <button
          type="submit"
          className="w-full rounded bg-[var(--color-accent)] px-4 py-2 text-base font-bold text-[var(--color-panel)] transition hover:brightness-110"
        >
          入室
        </button>
      </form>
    </div>
  );
};

/* -----------------------------------------------------------------------------
 * entry-preview-members-2026-07-21: 入室前のキャンバスプレビュー + 参加者一覧
 *
 * - preview: 入室プレビューの鮮度対処 C (undo-conflict-aware-2026-07-12.md
 *   「入室プレビューの鮮度」節、2026-07-23 確定) で **単枚合成 PNG (`url`) を
 *   優先表示**する。単枚パスは `layerUrls` を一切 fetch しない (帯域改善が目的
 *   の一つ). `url` が無い (旧 server / preview-only job 未生成) か読み込みに
 *   失敗したら、従来の layer PNG (下層 → 上層) `<img>` 絶対配置重ねにフォール
 *   バックする (snapshot は素直な source-over 重ねなので描画エンジン不要).
 *   いずれの経路も snapshot 由来なので「少し前のキャンバスです」の注記を添え
 *   る. 鮮度は Route 1 (snapshot 遅行, 2026-07-23) 以降、活発な部屋で最大
 *   ~15 分 (5 分トリガ × horizon 10 分遅行). 具体分数は文言に出さない方針.
 * - preview null = **オーナーが Off** (e555dd7 で契約明確化) → members の null
 *   と同じくブロックごと非表示. On だが snapshot 未生成 (作成直後 / presign
 *   失敗 degrade) は `url` なし + 空 layerUrls で届き、控えめなプレースホルダ
 *   を出す.
 * - members: displayName の列挙のみ. null (= Off) は非表示、[] は「いまは誰も
 *   いません」.
 * - 取得中 (info null) / 取得失敗 ('error') / 両方 Off は section ごと非表示
 *   (入室フォームの主目的 = 入室を阻害しない).
 * --------------------------------------------------------------------------- */

/** プレビュー枠の高さ上限 (rem). 正方形キャンバスでフォーム内に収まるサイズ. */
const PREVIEW_MAX_HEIGHT_REM = 11;

const EntryInfoSection = ({
  info,
}: {
  readonly info: FetchEntryInfoOutcome | null;
}): React.ReactNode => {
  if (info === null || info.outcome === 'error') return null;
  // あいことば付き部屋: preview / members は Off と同じ形 (null) で届くので、
  // それらを個別に判定する前に一文だけ出して早期 return する.
  if (info.entryPasswordRequired) {
    return (
      <div className="mb-4">
        <p className="text-xs text-[var(--color-text-muted)]">
          あいことばが必要な部屋です。入室するとキャンバスと参加者が見えます。
        </p>
      </div>
    );
  }
  // 両方 Off (preview null + members null) はセクションごと非表示 (余白も残さない).
  if (info.preview === null && info.members === null) return null;
  return (
    <div className="mb-4">
      <EntryPreviewBlock preview={info.preview} />
      <EntryMembersBlock members={info.members} membersTotal={info.membersTotal} />
    </div>
  );
};

const PreviewPlaceholder = (): React.ReactNode => (
  <div className="grid h-16 place-items-center rounded border border-white/10 bg-[var(--color-bg-soft)]">
    <p className="text-xs text-[var(--color-text-muted)]">キャンバスのプレビューはありません</p>
  </div>
);

/** 見た目枠 (aspectRatio + 幅上限) は単枚 / 重ね表示で共有する. */
const PreviewFrame = ({
  width,
  height,
  children,
}: {
  readonly width: number;
  readonly height: number;
  readonly children: React.ReactNode;
}): React.ReactNode => (
  <div className="mb-3">
    <div
      role="img"
      aria-label="少し前のキャンバスのプレビュー"
      // aspect-ratio を保ちながら「フォーム幅」と「高さ上限」の両方に収める:
      // width = min(100%, 上限高さ × アスペクト比) なら height は常に上限以下
      // かつ縦横比が正確に保たれる (max-h との併用だと aspect-ratio が破れる).
      className="relative mx-auto overflow-hidden rounded border border-white/10 bg-white"
      style={{
        aspectRatio: `${width} / ${height}`,
        width: `min(100%, calc(${PREVIEW_MAX_HEIGHT_REM}rem * ${width} / ${height}))`,
      }}
    >
      {children}
    </div>
    <p className="mt-1 text-center text-xs text-[var(--color-text-muted)]">
      少し前のキャンバスです
    </p>
  </div>
);

const EntryPreviewBlock = ({
  preview,
}: {
  readonly preview: EntryInfoPreview | null;
}): React.ReactNode => {
  // 単枚合成 PNG (preview.url) の読み込み失敗フラグ. 失敗したら layerUrls
  // 重ねへフォールバック (それも無ければプレースホルダ).
  const [singleFailed, setSingleFailed] = useState(false);
  // presigned URL の TTL 切れ (60s) 等で load 失敗した layer は個別に隠す.
  // 全 layer が失敗したらプレースホルダに degrade (壊れた画像アイコンを出さない).
  const [failedIndices, setFailedIndices] = useState<ReadonlySet<number>>(new Set());

  // null = オーナーがプレビュー表示を Off にしている (e555dd7 で契約明確化:
  // null は Off 専用) → プレースホルダも出さずブロックごと非表示.
  if (preview === null) return null;

  // 単枚パス優先: url があり、かつまだ読み込み失敗していなければ layerUrls
  // には一切触れない (fetch しない = 帯域改善).
  if (typeof preview.url === 'string' && preview.url.length > 0 && !singleFailed) {
    return (
      <PreviewFrame width={preview.width} height={preview.height}>
        <img
          src={preview.url}
          alt=""
          draggable={false}
          className="absolute inset-0 h-full w-full"
          onError={() => setSingleFailed(true)}
        />
      </PreviewFrame>
    );
  }

  // ここに来るのは (a) 単枚なし、(b) 単枚読み込み失敗のいずれか — layerUrls
  // フォールバック.

  // 空 layerUrls (作成直後 / presign 失敗 degrade) → 控えめなプレースホルダ.
  if (preview.layerUrls.length === 0) {
    return (
      <div className="mb-3">
        <PreviewPlaceholder />
      </div>
    );
  }

  const allFailed = failedIndices.size >= preview.layerUrls.length;
  if (allFailed) {
    return (
      <div className="mb-3">
        <PreviewPlaceholder />
      </div>
    );
  }

  return (
    <PreviewFrame width={preview.width} height={preview.height}>
      {preview.layerUrls.map((url, i) =>
        failedIndices.has(i) ? null : (
          <img
            key={url}
            src={url}
            alt=""
            draggable={false}
            className="absolute inset-0 h-full w-full"
            onError={() => {
              setFailedIndices((prev) => {
                const next = new Set(prev);
                next.add(i);
                return next;
              });
            }}
          />
        ),
      )}
    </PreviewFrame>
  );
};

const EntryMembersBlock = ({
  members,
  membersTotal,
}: {
  /** join 順の先頭 20 人まで (server 側 cap 済). */
  readonly members: readonly EntryInfoMember[] | null;
  /** cap 適用前の総人数. 旧 server (欠落) は null → members.length で degrade. */
  readonly membersTotal: number | null;
}): React.ReactNode => {
  // null = オーナーが参加者リスト表示を Off にしている → 何も出さない.
  if (members === null) return null;

  if (members.length === 0) {
    return <p className="text-xs text-[var(--color-text-muted)]">いまは誰もいません</p>;
  }

  // ヘッダの人数は総数 (membersTotal). 旧 server で欠落なら手元の件数で degrade.
  const total = membersTotal ?? members.length;
  // cap (先頭 20 人) からあふれた分は「+N人」の省略チップで示す.
  const overflow = Math.max(0, total - members.length);

  // displayName は重複しうる (「名無しさん」×2 等) ので出現回数で key を安定化.
  const seen = new Map<string, number>();
  const items = members.map((m) => {
    const nth = (seen.get(m.displayName) ?? 0) + 1;
    seen.set(m.displayName, nth);
    return { key: `${m.displayName}#${nth}`, name: m.displayName };
  });

  return (
    <div>
      <p className="text-xs text-[var(--color-text-muted)]">いまいる人 ({total})</p>
      <ul aria-label="参加中の人" className="mt-1 flex flex-wrap gap-1">
        {items.map((item) => (
          <li
            key={item.key}
            className="max-w-full truncate rounded border border-white/10 bg-[var(--color-bg-soft)] px-1.5 py-0.5 text-xs"
          >
            {item.name}
          </li>
        ))}
        {overflow > 0 && (
          <li
            // 省略チップ: 通常チップと区別できる控えめトーン (破線 border +
            // muted 文字 + 背景なし).
            className="rounded border border-white/20 border-dashed px-1.5 py-0.5 text-[var(--color-text-muted)] text-xs"
          >
            +{overflow}人
          </li>
        )}
      </ul>
    </div>
  );
};
